import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Paper, Container, Stack, Alert, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../../config/api';

const RegisterPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Form State
    const [formData, setFormData] = useState({
        schoolCode: '', // Changed from tenantId
        studentId: '',
        studentLastName: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        password: '',
        confirmPassword: ''
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);
        try {
            await axios.post(getApiUrl('auth/register/parent'), formData);
            // Auto login or redirect to login
            navigate('/login');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Registration failed.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#0B0F19',
                backgroundImage: 'radial-gradient(circle at 50% -20%, #1a237e 0%, #0B0F19 70%)',
                py: 4
            }}
        >
            <Box sx={{ position: 'absolute', top: 20, left: 20 }}>
                <Button 
                    startIcon={<ArrowBackIcon />} 
                    onClick={() => navigate('/')}
                    sx={{ color: 'white', textTransform: 'none', fontWeight: 600 }}
                >
                    Back to Website
                </Button>
            </Box>
            <Container maxWidth="md">
                <Paper
                    elevation={10}
                    sx={{
                        p: 5,
                        borderRadius: 4,
                        bgcolor: 'rgba(255,255,255,0.05)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }}
                >
                    <Stack spacing={4}>
                        <Box textAlign="center">
                            <Typography variant="h4" fontWeight="bold" color="white">Parent Registration</Typography>
                            <Typography variant="body2" color="rgba(255,255,255,0.6)">Link your account to your student profile</Typography>
                        </Box>
 
                        {error && <Alert severity="error">{error}</Alert>}
 
                        <form onSubmit={handleRegister}>
                            <Stack spacing={3}>
                                {/* Step 1: School Identification */}
                                <Box sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <Typography variant="subtitle1" color="#ffab00" fontWeight="bold" gutterBottom>Step 1: School Identification</Typography>
                                    
                                    {/* Welcome email tip banner */}
                                    <Box sx={{
                                        display: 'flex', alignItems: 'flex-start', gap: 1.5,
                                        p: 1.5, mb: 2, borderRadius: 2,
                                        bgcolor: 'rgba(255, 171, 0, 0.08)',
                                        border: '1px solid rgba(255, 171, 0, 0.3)'
                                    }}>
                                        <Typography fontSize={20} lineHeight={1}>🔒</Typography>
                                        <Box>
                                            <Typography variant="body2" color="#ffca28" fontWeight="bold">Secure Registration Required</Typography>
                                            <Typography variant="caption" color="rgba(255,255,255,0.6)">
                                                Please check the welcome email sent to your inbox. It contains a unique **Secure School Code** required to link your account.
                                            </Typography>
                                        </Box>
                                    </Box>
 
                                    <TextField
                                        name="schoolCode" label="Secure School Code" variant="outlined" fullWidth required
                                        placeholder="E.g. SCH-7X24"
                                        value={formData.schoolCode} onChange={handleChange}
                                        helperText="Find this 6-character code in your welcome email."
                                        FormHelperTextProps={{ style: { color: 'rgba(255,255,255,0.5)' } }}
                                        InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                        InputProps={{ style: { color: 'white' }, sx: { bgcolor: 'rgba(255,255,255,0.05)' } }}
                                    />
                                </Box>

                                {/* Student Verification Section */}
                                <Box sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <Typography variant="subtitle1" color="#ffab00" fontWeight="bold" gutterBottom>Step 2: Student Verification</Typography>
                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                        <TextField
                                            name="studentId" label="Student ID Number" variant="outlined" fullWidth required
                                            value={formData.studentId} onChange={handleChange}
                                            helperText="Also found in your welcome email"
                                            FormHelperTextProps={{ style: { color: 'rgba(255,255,255,0.4)' } }}
                                            InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                            InputProps={{ style: { color: 'white' }, sx: { bgcolor: 'rgba(255,255,255,0.05)' } }}
                                        />
                                        <TextField
                                            name="studentLastName" label="Student Surname" variant="outlined" fullWidth required
                                            value={formData.studentLastName} onChange={handleChange}
                                            InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                            InputProps={{ style: { color: 'white' }, sx: { bgcolor: 'rgba(255,255,255,0.05)' } }}
                                        />
                                    </Stack>
                                </Box>

                                {/* Parent Details Section */}
                                <Box sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <Typography variant="subtitle1" color="#ffab00" fontWeight="bold" gutterBottom>Step 3: Parent Details</Typography>
                                    <Stack spacing={2}>
                                        <Stack direction="row" spacing={2}>
                                            <TextField
                                                name="firstName" label="First Name" variant="outlined" fullWidth required
                                                value={formData.firstName} onChange={handleChange}
                                                InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                                InputProps={{ style: { color: 'white' }, sx: { bgcolor: 'rgba(255,255,255,0.05)' } }}
                                            />
                                            <TextField
                                                name="lastName" label="Surname" variant="outlined" fullWidth required
                                                value={formData.lastName} onChange={handleChange}
                                                InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                                InputProps={{ style: { color: 'white' }, sx: { bgcolor: 'rgba(255,255,255,0.05)' } }}
                                            />
                                        </Stack>
                                        <TextField
                                            name="email" label="Email Address" type="email" variant="outlined" fullWidth required
                                            value={formData.email} onChange={handleChange}
                                            InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                            InputProps={{ style: { color: 'white' }, sx: { bgcolor: 'rgba(255,255,255,0.05)' } }}
                                        />
                                        <TextField
                                            name="phone" label="Phone Number" variant="outlined" fullWidth required
                                            value={formData.phone} onChange={handleChange}
                                            InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                            InputProps={{ style: { color: 'white' }, sx: { bgcolor: 'rgba(255,255,255,0.05)' } }}
                                        />
                                        <TextField
                                            name="address" label="Residential Address" variant="outlined" multiline rows={2} fullWidth required
                                            value={formData.address} onChange={handleChange}
                                            InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                            InputProps={{ style: { color: 'white' }, sx: { bgcolor: 'rgba(255,255,255,0.05)' } }}
                                        />
                                    </Stack>
                                </Box>

                                {/* Security Section */}
                                <Box sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <Typography variant="subtitle1" color="#ffab00" fontWeight="bold" gutterBottom>Step 4: Security</Typography>
                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            name="password" label="Password" type="password" variant="outlined" fullWidth required
                                            value={formData.password} onChange={handleChange}
                                            InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                            InputProps={{ style: { color: 'white' }, sx: { bgcolor: 'rgba(255,255,255,0.05)' } }}
                                        />
                                        <TextField
                                            name="confirmPassword" label="Confirm Password" type="password" variant="outlined" fullWidth required
                                            value={formData.confirmPassword} onChange={handleChange}
                                            InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                            InputProps={{ style: { color: 'white' }, sx: { bgcolor: 'rgba(255,255,255,0.05)' } }}
                                        />
                                    </Stack>
                                </Box>

                                <Button
                                    type="submit" variant="contained" size="large" fullWidth disabled={loading}
                                    sx={{ bgcolor: '#ffab00', color: 'black', fontWeight: 'bold', py: 1.5, '&:hover': { bgcolor: '#ffca28' } }}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Register Account'}
                                </Button>
                            </Stack>
                        </form>

                        <Box textAlign="center">
                            <Typography variant="body2" color="rgba(255,255,255,0.6)">
                                Already have an account?{' '}
                                <Link to="/login" style={{ color: '#ffab00', textDecoration: 'none', fontWeight: 'bold' }}>
                                    Sign In
                                </Link>
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
};

export default RegisterPage;
